/**
 * Teardown for the marketing-site `startProperties` hand-off.
 *
 * The hand-off (locale, consent, flow, package, template, market ids, tracking) is pre-extracted at
 * boot (`src/dev.js`) into durable per-field `sessionStorage` keys so it survives the auth-gate
 * `/login` redirect, registration, the session mint, and reloads. sessionStorage is tab-scoped but
 * is NOT cleared on the SPA logout / impersonation owner-swap (no page reload), so on a SHARED
 * browser a second owner signing in (same tab) would otherwise inherit the FIRST owner's stale
 * hand-off — leaking their campaign ids / flow / tracking into a fresh account (wrong attribution).
 *
 * {@link clearStartPropertiesHandoff} drops every durable key. It's called from the central teardown
 * chokepoints (`store/index.ts`: the `logout` listener + the identity-change listener), which fire
 * only at session end / owner switch — never on a fresh lead's first login (that path keeps the
 * hand-off for the configurator to consume). The one-shot keys (raw stash, package, template,
 * tracking) are also cleared by their own consumers mid-session; this is the backstop for anything
 * not yet consumed at logout / owner switch.
 */
import { START_FLOW_KEY } from './configurator-flow';
import { START_CID_KEY, START_CPID_KEY, START_CURRENCY_KEY, START_OR_KEY } from './start-properties-market';
import { START_TRACKING_KEY } from './start-tracking';

/** Keys declared under `nova/pages/**` — literal-mirrored here (importing a page module into the
 *  store layer would invert the dependency). The canonical source of truth is `src/dev.js`; keep in
 *  sync when a hand-off field is added. */
const NOVA_HANDOFF_KEYS: readonly string[] = [
  'ows-start-properties', // the raw `startProperties` stash
  'ows-start-selected-package',
  'ows-start-locale-override',
  'ows-start-template-seed',
  'ows-start-template-skin',
  'ows-start-template-art-style',
];

/** Every durable `startProperties` hand-off key (the util-owned ones + the nova-page ones). */
export const START_HANDOFF_KEYS: readonly string[] = [
  START_FLOW_KEY,
  START_OR_KEY,
  START_CURRENCY_KEY,
  START_CPID_KEY,
  START_CID_KEY,
  START_TRACKING_KEY,
  ...NOVA_HANDOFF_KEYS,
];

/** Remove every durable `startProperties` hand-off key from sessionStorage (best-effort). */
export function clearStartPropertiesHandoff(): void {
  try {
    START_HANDOFF_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* sessionStorage unavailable — nothing to clear. */
  }
}

export default clearStartPropertiesHandoff;
